import "./BuyInputSection.scss";
import { Trans } from "@lingui/macro";
import cx from "classnames";
import React, { useRef, ReactNode, ChangeEvent, useCallback, useMemo } from "react";

import { PERCENTAGE_SUGGESTIONS } from "config/ui";

import NumberInput from "components/NumberInput/NumberInput";

/**
 * TODO: Change name prop bottomRight -> bottomLeft
 *       Change name prop bottomLeft -> bottomRight
 * This is a breaking change, so it should be done in a separate PR.
 */

type Props = {
  topLeftLabel: string;
  bottomLeftValue?: string;
  isBottomLeftValueMuted?: boolean;
  bottomRightLabel?: string;
  bottomRightValue?: string;
  onClickBottomRightLabel?: () => void;
  topRightLabel?: string;
  topRightValue?: ReactNode;
  topRightContent?: ReactNode;
  onClickTopRightLabel?: () => void;
  inputValue?: number | string;
  onInputValueChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  maxButtonPosition?: "bottom-right" | "top-right";
  onClickMax?: () => void;
  onFocus?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  shouldClosestLabelTriggerMax?: boolean;
  children?: ReactNode;
  showPercentSelector?: boolean;
  onPercentChange?: (percentage: number) => void;
  qa?: string;
  isDisabled?: boolean;
  leftColumnMinWidth?: number;
};

function getMaxButtonPosition({
  maxButtonPosition: maybeMaxButtonPosition,
  onClickMax,
  shouldClosestLabelTriggerMax = true,
  topRightLabel,
  topRightValue,
  bottomRightLabel,
  bottomRightValue,
}: Props) {
  let maxPosition = "bottom-right";
  if (maybeMaxButtonPosition) {
    maxPosition = maybeMaxButtonPosition;
  } else if (onClickMax && shouldClosestLabelTriggerMax) {
    if (topRightLabel || topRightValue) {
      maxPosition = "top-right";
    } else if (bottomRightLabel || bottomRightValue) {
      maxPosition = "bottom-right";
    }
  }
  return maxPosition;
}

export default function BuyInputSection(props: Props) {
  const {
    topLeftLabel,
    bottomLeftValue,
    isBottomLeftValueMuted = false,
    bottomRightLabel,
    bottomRightValue,
    onClickBottomRightLabel,
    topRightLabel,
    topRightValue,
    topRightContent,
    onClickTopRightLabel,
    inputValue,
    onInputValueChange,
    onClickMax,
    onFocus,
    children,
    showPercentSelector,
    onPercentChange,
    qa,
    isDisabled = false,
    leftColumnMinWidth,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const maxButtonPosition = getMaxButtonPosition(props);
  const leftColumnStyle = useMemo(
    () => (leftColumnMinWidth ? { width: leftColumnMinWidth } : undefined),
    [leftColumnMinWidth]
  );

  const handleOnFocus = useCallback(() => {
    if (onFocus) onFocus();
  }, [onFocus]);

  const handleBoxClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onUserInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onInputValueChange) {
        onInputValueChange(e);
      }
    },
    [onInputValueChange]
  );

  const handleMaxClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      if (onClickMax) {
        e.stopPropagation();
        onClickMax();
      }
    },
    [onClickMax]
  );

  const handleTopRightClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClickTopRightLabel) {
        e.stopPropagation();
        onClickTopRightLabel();
      } else if (onClickMax && maxButtonPosition === "top-right") {
        e.stopPropagation();
        onClickMax();
      }
    },
    [onClickTopRightLabel, onClickMax, maxButtonPosition]
  );

  const handleBottomRightClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClickBottomRightLabel) {
        e.stopPropagation();
        onClickBottomRightLabel();
      } else if (onClickMax && maxButtonPosition === "bottom-right") {
        e.stopPropagation();
        onClickMax();
      }
    },
    [onClickBottomRightLabel, onClickMax, maxButtonPosition]
  );

  const showInlinePercentSelector = showPercentSelector && onPercentChange;
  const hasBottomRightMeta = Boolean(
    bottomRightLabel || bottomRightValue || (onClickMax && maxButtonPosition === "bottom-right")
  );
  const showFooter = Boolean(bottomLeftValue || hasBottomRightMeta || showInlinePercentSelector);

  return (
    <div data-qa={qa}>
      <div
        className={cx(
          `flex min-h-[104px] cursor-text flex-col gap-10 rounded-12 border border-solid border-white-alpha-4
          bg-white-alpha-2 px-16 py-10 text-12 leading-[16px] shadow-[inset_0_0_0_1px] shadow-[transparent]`,
          {
            "border-2 border-gray-darker bg-slate-800": isDisabled,
            "hover:[&:not(:focus-within)]:shadow-[rgba(47, 78, 54, 0.4)] focus-within:shadow-primary-alpha-40":
              !isDisabled,
          }
        )}
        onClick={handleBoxClick}
      >
        {(topLeftLabel || topRightLabel || topRightValue || (onClickMax && maxButtonPosition === "top-right")) && (
          <div className="flex items-center justify-between gap-12">
            <div data-label="left" className="text-tiny text-nowrap font-[500] text-gray-lighter">
              {topLeftLabel}
            </div>

            {(topRightLabel ||
              topRightValue ||
              topRightContent ||
              (onClickMax && maxButtonPosition === "top-right")) && (
              <div
                data-label="right"
                className={cx(
                  "flex items-baseline justify-end gap-8 text-right",
                  (onClickTopRightLabel || (onClickMax && maxButtonPosition === "top-right")) && "cursor-pointer"
                )}
                onClick={handleTopRightClick}
              >
                {topRightContent ? (
                  topRightContent
                ) : (
                  <>
                    {topRightLabel && <span className="text-gray-lighter">{topRightLabel}:</span>}
                    {topRightValue && <span>{topRightValue}</span>}
                  </>
                )}
                {onClickMax && maxButtonPosition === "top-right" && (
                  <button
                    type="button"
                    className="InputSection-actionButton"
                    onClick={handleMaxClick}
                    data-qa="input-max"
                  >
                    <Trans>MAX</Trans>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-12">
          <div className="flex min-w-0 flex-col justify-center gap-8" style={leftColumnStyle}>
            <div className="shrink-0 text-24 leading-[24px]">{children}</div>
          </div>

          <div className="flex min-w-0 flex-1 items-center">
            <NumberInput
              value={inputValue}
              className="h-28 w-full min-w-0 p-0 text-right text-[15px] !leading-[24px] text-white outline-none placeholder:!text-gray-lighter"
              inputRef={inputRef}
              onValueChange={onUserInput}
              onFocus={handleOnFocus}
              placeholder="0.0"
              qa={qa ? qa + "-input" : undefined}
              isDisabled={isDisabled}
            />
          </div>
        </div>

        {showFooter && (
          <div className="flex flex-wrap items-center justify-between gap-10 border-t border-white-alpha-4 pt-8">
            <div
              className={cx("text-small min-w-0", {
                "text-gray-lighter": isBottomLeftValueMuted,
                "text-white": !isBottomLeftValueMuted,
              })}
            >
              {bottomLeftValue || ""}
            </div>

            <div className="flex min-w-0 flex-wrap items-center justify-end gap-6">
              {hasBottomRightMeta && (
                <div
                  className={cx(
                    "flex min-w-0 items-baseline gap-4 rounded-8 border border-white-alpha-4 bg-white-alpha-2 px-8 py-4",
                    (onClickBottomRightLabel || (onClickMax && maxButtonPosition === "bottom-right")) &&
                      "cursor-pointer"
                  )}
                  onClick={handleBottomRightClick}
                >
                  {bottomRightLabel && (
                    <span className="whitespace-nowrap text-white-alpha-40">{bottomRightLabel}:</span>
                  )}
                  {bottomRightValue && (
                    <span className="text-white-alpha-70 max-w-[180px] truncate" title={bottomRightValue}>
                      {bottomRightValue}
                    </span>
                  )}

                  {onClickMax && maxButtonPosition === "bottom-right" && (
                    <button
                      type="button"
                      className="InputSection-actionButton"
                      onClick={handleMaxClick}
                      data-qa="input-max"
                    >
                      <Trans>MAX</Trans>
                    </button>
                  )}
                </div>
              )}

              {showInlinePercentSelector && (
                <ul className="PercentSelector">
                  {PERCENTAGE_SUGGESTIONS.map((percentage) => (
                    <li key={percentage}>
                      <button
                        type="button"
                        className="PercentSelector-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPercentChange?.(percentage);
                        }}
                        data-qa={`${qa}-percent-selector-${percentage}`}
                      >
                        {percentage}%
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
